import { Ref, computed, ref } from "vue";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import { useDialog } from "primevue/usedialog";
import { useHotKeys } from "./hotkeys";
import Keybindings from "@/components/Keybindings.vue";

const isMac = navigator.userAgent.toLowerCase().includes("mac");

export type StringKeyBindingExplanation = {
    keys: string;
    explanation: string;
};

const _explanations: Ref<Record<string, StringKeyBindingExplanation[]>> = ref({
    "Editor": [
        {
            keys: "ControlOrMeta+C",
            explanation: "Copy selected",
        },
        {
            keys: "ControlOrMeta+X",
            explanation: "Cut selected",
        },
        {
            keys: "ControlOrMeta+V",
            explanation: "Paste from clipboard",
        },
        {
            keys: "ControlOrMeta+Shift+Z",
            explanation: "Redo last action",
        },
        {
            keys: "ControlOrMeta+Z",
            explanation: "Undo last action",
        },
    ],
    "Dialog": [
        {
            keys: "ControlOrMeta+Enter",
            explanation: "Save",
        },
        {
            keys: "Escape",
            explanation: "Close",
        },
    ],
    "BPMN": [
        {
            keys: "ControlOrMeta+Z",
            explanation: "Undo last step",
        },
        {
            keys: "ControlOrMeta+Shift+Z",
            explanation: "Redo last step",
        },
        {
            keys: "ControlOrMeta+C",
            explanation: "Copy selected elements",
        },
        {
            keys: "ControlOrMeta+X",
            explanation: "Cut selected elements",
        },
        {
            keys: "ControlOrMeta+V",
            explanation: "Paste copied elements",
        },
        {
            keys: "ControlOrMeta+A",
            explanation: "Select all elements",
        },
        {
            keys: "ControlOrMeta+E",
            explanation: "Search labels",
        },
        {
            keys: "S",
            explanation: "Activate space tool",
        },
        {
            keys: "L",
            explanation: "Activate lasso tool",
        },
        {
            keys: "H",
            explanation: "Activate hand tool",
        },
        {
            keys: "C",
            explanation: "Activate global connect tool",
        },
        {
            keys: "E",
            explanation: "Activate direct editing",
        },
        {
            keys: "R",
            explanation: "Activate replace element",
        },
    ],
});

const replaceControlOrMeta = (keys: string) => {
    if (isMac) {
        return keys.replaceAll("ControlOrMeta", "⌘");
    } else {
        return keys.replaceAll("ControlOrMeta", "Control");
    }
};

export function useKeybindingsExplanation() {
    /**
     * Listen to keyboard shortcuts for the dialogExplanation.
     */
    const { addHotKey, hotKeys } = useHotKeys();
    const dialog = useDialog();
    const dialogRef = ref<DynamicDialogInstance & { visible?: boolean }>();
    const isVisible = computed(() => !!dialogRef.value?.visible);

    addHotKey("Shift+?", (event) => {
        if (!isVisible.value) {
            event.preventDefault();
            open();
        }
    });

    addHotKey("Escape", (event) => {
        if (isVisible.value) {
            event.preventDefault();
            close();
        }
    });

    /**
     * Toggle visibility of explanations.
     */
    const open = () => {
        dialogRef.value = dialog.open(Keybindings, {
            props: {
                header: "Keyboard shortcuts",
                style: {
                    maxWidth: "1920px",
                    width: "80%",
                },
                modal: true,
                maximizable: false,
                draggable: false,
            },
            data: stringExplanations.value,
        });
    };

    const close = () => dialogRef.value?.close();

    const stringExplanations = computed<
        Record<string, StringKeyBindingExplanation[]>
    >(() => {
        const _stringExplanations: Record<
            string,
            StringKeyBindingExplanation[]
        > = {};

        for (const group in _explanations.value) {
            _stringExplanations[group] = [];
            for (const { keys, explanation } of _explanations.value[group]) {
                _stringExplanations[group].push({
                    keys: replaceControlOrMeta(keys),
                    explanation: explanation,
                });
            }
        }

        // Add current hotKeys as well.
        _stringExplanations["Current"] = [];
        for (const [keys, value] of Object.entries(hotKeys.value)) {
            if (!value.explanation) {
                continue;
            }
            _stringExplanations["Current"].push({
                keys: replaceControlOrMeta(keys),
                explanation: value.explanation,
            });
        }

        return _stringExplanations;
    });

    return { open, close };
}
